import { httpClient, ApiError } from "@/shared/api/http-client";
import type { SessionUser, AuthRejection } from "@/shared/auth/auth-context";
import { parseRole, Role } from "@/shared/types/role";

/**
 * Shape of `GET /api/auth/me` (see growth-engine-api `auth.controller.ts`).
 * Hand-written rather than imported from the Orval-generated surface, which is
 * gitignored and absent in CI (see the app README → generated-types note). Keep
 * in sync with the API DTO.
 */
export const AUTH_ME_PATH = "/api/auth/me";

interface MeTenant {
  id: string;
  name: string;
  slug: string;
  role: string;
}

interface MeResponse {
  userId: string;
  email: string;
  superadmin: boolean;
  activeTenantId: string | null;
  role: string;
  tenants: MeTenant[];
}

/**
 * Resolve the Growth session for the Firebase-authenticated caller. The Bearer
 * token and active X-Tenant-Id are attached by httpClient. Throws ApiError on
 * non-2xx (401/403/404 = not provisioned, 5xx = backend trouble) and a plain
 * Error on network failure — both classified by `classifyVerificationError`.
 */
export async function fetchSession(): Promise<SessionUser> {
  const me = await httpClient<MeResponse>(AUTH_ME_PATH);
  return {
    userId: me.userId,
    email: me.email,
    superadmin: me.superadmin,
    // The API only ever returns known roles; default defensively to MARKETING
    // (the least-privileged role) if an unknown value ever appears.
    role: parseRole(me.role) ?? Role.MARKETING,
    activeTenantId: me.activeTenantId,
    tenants: me.tenants.map((t) => ({
      id: t.id,
      name: t.name,
      slug: t.slug,
      role: parseRole(t.role) ?? Role.MARKETING,
    })),
  };
}

/**
 * Map a verification failure to the reason the UI should show. Only a
 * definitive identity rejection (401/403/404) means "this account can't use
 * Growth — retrying won't help", so the caller signs the user out. Everything
 * else (5xx, network, parse) is transient: deny access but keep the session.
 */
export function classifyVerificationError(err: unknown): AuthRejection {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403 || err.status === 404)) {
    return "not-provisioned";
  }
  return "verification-failed";
}
